import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, GOOGLE_AUTH_URL } from "../lib/api";
import { GoogleIcon } from "../components/GoogleIcon";
import { PLAN_PENDIENTE_KEY } from "../lib/planPendiente";

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function Registro() {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan");

  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  function guardarPlanPendiente() {
    if (plan) localStorage.setItem(PLAN_PENDIENTE_KEY, plan);
  }

  function handleGoogleClick() {
    guardarPlanPendiente();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);

    const nombreTrim = nombre.trim();
    if (nombreTrim.length < 2) {
      setFormError("Ingresa tu nombre completo.");
      return;
    }
    if (!validateEmail(email.trim())) {
      setFormError("Ingresa un email válido.");
      return;
    }
    if (password.length < 8) {
      setFormError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      setFormError("Las contraseñas no coinciden.");
      return;
    }

    guardarPlanPendiente();

    setLoading(true);
    const r = await api.register({ email: email.trim(), password, nombre: nombreTrim });
    setLoading(false);

    if (r.ok) {
      setRegistered(true);
    } else {
      setFormError(r.error.message || "No pudimos crear tu cuenta. Intenta de nuevo.");
    }
  }

  return (
    <div style={{ fontFamily: "var(--font-body)", position: "relative", minHeight: "100vh", overflow: "hidden", color: "var(--ink)" }}>
      {/* blurred backdrop (landing peeking through) */}
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(900px 520px at 75% 0%, #4a4339, #33302B)", filter: "blur(2px)" }}>
        <div className="blob" style={{ position: "absolute", left: -60, top: 140, width: 320, height: 320, background: "var(--sage)", borderRadius: "60% 40% 50% 50%/55% 50% 50% 45%", opacity: 0.2, filter: "blur(20px)" }} />
        <div className="blob" style={{ position: "absolute", right: 80, bottom: -40, width: 280, height: 280, background: "#C99A6E", borderRadius: "50%", opacity: 0.2, filter: "blur(20px)", animationDelay: "-3s" }} />
        <img src="/logo-psicodosis.png" style={{ position: "absolute", right: -100, top: 60, width: 520, opacity: 0.08 }} className="hide-mobile" />
        <div className="hide-mobile" style={{ position: "absolute", left: 60, top: 200, maxWidth: 420, opacity: 0.5 }}>
          <div className="fluid-heading" style={{ fontFamily: "var(--font-display)", fontWeight: 800, lineHeight: 1.04, letterSpacing: "-0.03em", color: "var(--on-dark)" }}>
            No tienes que hacerlo solo
          </div>
        </div>
      </div>
      <div style={{ position: "absolute", inset: 0, background: "rgba(40,35,30,.35)" }} />

      {/* glass modal */}
      <div style={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <div className="modal-glass" style={{ maxWidth: 420, animation: "modalIn 0.4s var(--ease)" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginBottom: 26 }}>
            <img src="/logo-psicodosis.png" alt="PzicoDosis" style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 14 }} />
            <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 27, letterSpacing: "-0.02em", margin: "0 0 5px" }}>
              Únete a PzicoDosis
            </h1>
            {!registered && (
              <p style={{ fontSize: 14, color: "var(--muted)", margin: 0 }}>
                Crea tu cuenta para empezar
              </p>
            )}
          </div>

          {registered ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>📬</div>
              <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, margin: "0 0 10px" }}>
                Revisa tu email
              </h2>
              <p style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.6, margin: "0 0 22px" }}>
                Te enviamos un link de verificación a <strong>{email.trim()}</strong>. Ábrelo para activar tu cuenta — luego ya podrás iniciar sesión.
              </p>
              <Link to="/login" className="btn btn-outline btn--full" style={{ textDecoration: "none" }}>
                Volver a iniciar sesión
              </Link>
            </div>
          ) : (
            <>
              <a href={GOOGLE_AUTH_URL} onClick={handleGoogleClick} className="btn btn-primary btn--full btn--lg" style={{ textDecoration: "none" }}>
                <GoogleIcon /> Continuar con Google
              </a>

              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "22px 0" }}>
                <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                <span style={{ fontSize: 12.5, color: "var(--muted-3)", fontWeight: 600 }}>o regístrate con tu email</span>
                <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              </div>

              {formError && (
                <div className="alert alert--error" role="alert" style={{ marginBottom: 16 }}>
                  ⚠ {formError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <label className="field-label">Nombre completo</label>
                <input
                  type="text" className="form-input" placeholder="Tu nombre"
                  value={nombre} onChange={(e) => setNombre(e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <label className="field-label">Email</label>
                <input
                  type="email" className="form-input" placeholder="tu@correo.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <label className="field-label">Contraseña</label>
                <input
                  type="password" className="form-input" placeholder="Mínimo 8 caracteres"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <label className="field-label">Confirmar contraseña</label>
                <input
                  type="password" className="form-input" placeholder="Repite tu contraseña"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ marginBottom: 20 }}
                />
                <button type="submit" className="btn btn-outline btn--full" disabled={loading}>
                  {loading ? <><span className="spinner" /> Creando cuenta…</> : "Crear cuenta"}
                </button>
              </form>

              <div style={{
                background: "rgba(184, 212, 200, 0.15)",
                border: "1px solid rgba(184, 212, 200, 0.3)",
                borderRadius: 10,
                padding: "12px 16px",
                fontSize: 13,
                color: "#6B8F82",
                margin: "20px 0 16px",
                textAlign: "left",
              }}>
                💡 Una vez dentro, puedes personalizar tu perfil, cambiar tu nombre y añadir una foto desde la sección "Mi perfil".
              </div>

              <p style={{ fontSize: 12.5, color: "var(--muted-3)", lineHeight: 1.5, textAlign: "center", margin: "0 0 20px" }}>
                Al registrarte aceptas nuestros Términos y Política de privacidad
              </p>

              <div style={{ textAlign: "center", fontSize: 14, color: "var(--muted)" }}>
                ¿Ya tienes cuenta?{" "}
                <Link to="/login" style={{ color: "var(--copper)", fontWeight: 700, textDecoration: "none" }}>
                  Inicia sesión
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
